import React, { useState, useEffect } from 'react';
import { DownloadOutlined, FileOutlined } from '@ant-design/icons';
import { Modal, Button, Tag, Table, Spin, message } from 'antd';
import apiClient from '../../api.js';

const FileAnalysisModal = ({
  title,
  modalState,
  setModalState,
  selectedFile,
}) => {
  const [analysisData, setAnalysisData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Fetch analysis data when modal opens and fileId is available
  useEffect(() => {
    if (modalState && selectedFile && selectedFile.id) {
      fetchAnalysisData();
    }
  }, [selectedFile && modalState]);

  const fetchAnalysisData = async () => {
    if (!selectedFile || !selectedFile.id) return;

    setLoading(true);
    try {
      const response = await apiClient.get(
        `/analysis/${selectedFile.id}/summary`
      );
      if (response.status / 100 == 2) {
        setAnalysisData(response.data);
      } else {
        message.error(`Error:${response.data['error']}`);
      }
    } catch (error) {
      console.error('Error fetching analysis data:', error);
      message.error('Failed to fetch analysis data');
      setAnalysisData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = e => {
    e.preventDefault();
    setModalState(false);
    setAnalysisData(null);
  };
  const handleDownloadFile = file => {
    if (!analysisData) {
      message.error('No analysis data available to download');
      return;
    }

    try {
      // Create CSV content from analysis data
      const tableData = getTableDataSource();
      
      // CSV headers
      const headers = ['Category', 'Solved', 'Unsolved', 'Total'];
      
      // Convert table data to CSV rows
      const csvRows = tableData.map(row => [
        row.category,
        row.solved,
        row.unsolved,
        row.solved + row.unsolved
      ]);
      
      // Combine headers and data
      const csvContent = [headers, ...csvRows]
        .map(row => row.map(cell => `"${cell}"`).join(','))
        .join('\n');
      
      // Add metadata as comments at the top
      const metadata = [
        `# Analysis Report for: ${file?.name || 'Unknown File'}`,
        `# File ID: ${file?.id || 'N/A'}`,
        `# Status: ${analysisData.status || 'Completed'}`,
        `# Progress: ${analysisData.progress || 100}%`,
        `# Generated: ${new Date().toISOString()}`,
        '#',
      ].join('\n');
      
      const fullCsvContent = metadata + '\n' + csvContent;
      
      // Create blob
      const blob = new Blob([fullCsvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);

      // Create temporary download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `analysis_${file?.name || file?.id || 'data'}.csv`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      message.success('Analysis data downloaded successfully as CSV!');
    } catch (error) {
      console.error('Error downloading analysis data:', error);
      message.error('Failed to download analysis data');
    }
  };

  // Transform API data to table format
  const getTableDataSource = () => {
    if (!analysisData || !analysisData.summary) return [];

    const summary = analysisData.summary;
    const dataSource = [];

    // Transform each category in the summary
    Object.keys(summary).forEach((category, index) => {
      const categoryData = summary[category];
      dataSource.push({
        key: (index + 1).toString(),
        category: category,
        solved: categoryData.total_solved || 0,
        unsolved: categoryData.total_unsolved || 0,
      });
    });

    return dataSource;
  };
  const columns = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
    },
    {
      title: 'Solved',
      dataIndex: 'solved',
      key: 'solved',
      width: 80,
      align: 'center',
      render: value => (
        <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{value}</span>
      ),
    },
    {
      title: 'Unsolved',
      dataIndex: 'unsolved',
      key: 'unsolved',
      width: 80,
      align: 'center',
      render: value => (
        <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>{value}</span>
      ),
    },
    {
      title: 'Total',
      key: 'total',
      width: 80,
      align: 'center',
      render: (_, record) => (
        <span style={{ fontWeight: 'bold' }}>
          {record.solved + record.unsolved}
        </span>
      ),
    },
  ];

  return (
    <Modal
      title={title}
      open={modalState}
      onCancel={handleModalClose}
      width={600}
      footer={[
        <Button key="close" onClick={handleModalClose}>
          Close
        </Button>,
        <Button
          key="download"
          type="default"
          icon={<DownloadOutlined />}
          onClick={() => handleDownloadFile(selectedFile)}
        >
          Download
        </Button>,
      ].filter(Boolean)}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <Spin size="large" />
          <p style={{ marginTop: '1rem' }}>Loading analysis data...</p>
        </div>
      ) : selectedFile && analysisData ? (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Tag color="green" style={{ marginRight: 8 }}>
              Status: {analysisData.status || 'Completed'}
            </Tag>
            <Tag color="blue">Progress: {analysisData.progress || 100}%</Tag>
          </div>

          <Table
            size="small"
            pagination={false}
            dataSource={getTableDataSource()}
            columns={columns}
          />
        </div>
      ) : (
        <>No Data</>
      )}
    </Modal>
  );
};

export default FileAnalysisModal;
